// Generated by Copilot- Start
/**
 * ============================================================================
 * CHANNELS.TS - Channel parsing, normalization, grouping, and genre addons
 * ============================================================================
 *
 * This file consolidates all channel-related operations:
 * - Channel parsing from CMS data
 * - Channel normalization (reducing payload size)
 * - Channel grouping by category
 * - Genre addon eligibility checks
 *
 * Replaces: channel-parser.ts, channels.ts, normalization-helper.ts, genre-addon-helper.ts
 */

import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import {
  ChannelCardV2,
  PackageSummary,
  PackageReference,
  ChannelAvailability,
  SvodPromoMap,
  ChannelsPartitionResult,
  HiddenCategoryGroup
} from "./types";
import { getAvailabilityText, getSvodPromoText } from "./packages";
import { getPackageIdsByType } from "../genre-helper";
import { isChannelInPack } from "../channel-pack-membership";
import type { IPackageConfig } from "../../types/global";
import { logAdapterEvent, logError } from "../logger";
import {
  isFastChannelForMyFree,
  resolveChannelCategoryKey,
  ChannelCategoryConfig
} from "../channels-service-helpers";

const HELPER_NAME = "channel-interactive-tool-v2/channels";

// ============================================================================
// SECTION 1: CHANNEL PARSING - Convert CMS data to ChannelCardV2 format
// ============================================================================

// Map legacy package IDs to correct package IDs
// CMS data sometimes has DTV_CHOICE instead of DTV_TV_CHOICE for satellite English packages
const LEGACY_PACKAGE_ID_MAP: Record<string, string> = {
  DTV_ENTERTAINMENT: "DTV_TV_ENTERTAINMENT",
  DTV_CHOICE: "DTV_TV_CHOICE",
  DTV_ULTIMATE: "DTV_TV_ULTIMATE",
  DTV_PREMIER: "DTV_TV_PREMIER",
  DTV_XTRA: "DTV_TV_XTRA"
};

/**
 * Normalize package ID by mapping legacy IDs to correct IDs
 */
const normalizePackageId = (pkgId: string): string => LEGACY_PACKAGE_ID_MAP[pkgId] || pkgId;

interface ParseChannelsV2Args {
  channels: any[];
  packages?: any[]; // Optional - only used for logging
  packageSummaries: Record<string, PackageSummary>;
  chnlSrc: string;
  categoryLookup: Record<string, KeyValuePair>;
  packageOrderByCategory?: Record<string, string[]>; // Optional - no longer used
  myFreeDTVConfig?: any;
  displayGenrePack?: boolean;
  showMyFreeDTVChannels?: boolean;
  isGenreVariant?: boolean;
  isSinglePackageVariant?: boolean;
  singlePackageConfig?: any;
  genreConfig?: Record<string, boolean>;
  svodDetails?: any;
  svodPcpDetails?: any[];
  genreAddonChannelSet?: Set<string>;
  packageConfig?: any[];
  /**
   * When true (genre variant), signaturePackages and genrePackages are omitted
   * from each ChannelCardV2 to reduce payload size.
   * These fields are only needed for the channel modal, which is disabled in genre variant.
   */
  stripPackageRefs?: boolean;
  /**
   * Ordered package-config category config (`{label, engCategories[]}`), the same
   * array the visible grid buckets against. Used to resolve each channel's single
   * representative `categoryId` from its multi-valued `categories[]` (flag-ON) so
   * the addon/hidden grouping + client search land on the SAME bucket key the grid
   * uses. Absent / no engCategories (flag-OFF/AEM) -> the channel's own categoryId
   * slug is kept untouched.
   */
  categoryConfig?: ChannelCategoryConfig[];
}

/**
 * Build signature and genre package lists for a channel with availability info
 * Also collects SVOD promo text for the svodPromoMap
 */
const buildPackageLists = (
  channel: any,
  packageSummaries: Record<string, PackageSummary>,
  chnlSrc: string,
  svodPromoMap: SvodPromoMap,
  genreConfig?: Record<string, boolean>,
  svodDetails?: any,
  svodPcpDetails?: any[]
) => {
  const signaturePackageList: any[] = [];
  const genrePackageList: any[] = [];
  const signaturePackages: PackageReference[] = []; // Enriched references with availability
  const genrePackages: PackageReference[] = []; // Enriched references with availability
  const processedIds = new Set<string>();

  // Flags for availability
  let hasIncludedPackages = false;
  let isAddonOnly = true;
  let isPromoOnly = true;

  // Determine allowed types for signature packages
  const allowedSignatureTypes = chnlSrc === "streamclup" ?
    ["internet"] :
    chnlSrc === "dtvclup" ?
      ["satellite"] :
      ["internet", "satellite"];

  const allowedGenreTypes = ["genre", "genre-mini"];

  // Helper to process packages with availability type
  const processPackages = (packages: string[], availability: ChannelAvailability) => {
    if (!packages || !Array.isArray(packages)) {
      return;
    }

    packages.forEach((rawPkgId: string) => {
      if (!rawPkgId || typeof rawPkgId !== 'string') {
        return;
      }

      // Normalize package ID (handle legacy IDs like DTV_CHOICE -> DTV_TV_CHOICE)
      const pkgId = normalizePackageId(rawPkgId);

      if (processedIds.has(pkgId)) {
        return;
      }

      const summary = packageSummaries[pkgId];
      if (!summary) {
        return;
      }

      const pkgType = summary.type;
      if (!pkgType) {
        return;
      }

      // Update availability flags
      if (availability === "included") {
        hasIncludedPackages = true;
        isAddonOnly = false;
        isPromoOnly = false;
      } else if (availability === "addon") {
        isPromoOnly = false;
      }

      // Priority order: included > SVOD promo > addon > promo
      let legalText = getAvailabilityText(availability);
      let svodPromoText: string | null = null;

      // Check if SVOD promo text applies (regardless of availability)
      // Some channels included in packages may still have promo text like "Get DIRECTV Sports Pack..."
      if (svodDetails || svodPcpDetails) {
        svodPromoText = getSvodPromoText(
          channel.channelNameLabel,
          summary.queryParam,
          availability,
          svodDetails,
          svodPcpDetails,
          chnlSrc
        );

        // Store SVOD promo text in the map for this channel + package combo
        if (svodPromoText) {
          if (!svodPromoMap[channel.channelNameLabel]) {
            svodPromoMap[channel.channelNameLabel] = {};
          }
          svodPromoMap[channel.channelNameLabel][pkgId] = svodPromoText;
          legalText = svodPromoText; // Use for legacy signaturePackageList
        }
      }

      const entry = {
        id: pkgId,
        displayLabel: summary.displayLabel || pkgId,
        packageLegal: legalText,
        ctaObj: summary.ctaObj || null,
        type: pkgType
      };

      // Add to signature list if matches type
      if (allowedSignatureTypes.includes(pkgType)) {
        signaturePackageList.push(entry);
        signaturePackages.push({
          id: pkgId,
          availability
        }); // Add enriched reference
        processedIds.add(pkgId);
      // Add to genre list if matches type and is enabled in genreConfig
      } else if (allowedGenreTypes.includes(pkgType)) {
        // Check if genreConfig allows this package
        const isEnabled = genreConfig ? genreConfig[pkgId] !== false : true;
        if (isEnabled) {
          // Use genrePackageLegal for genre packages instead of general legalText
          const genreEntry = {
            ...entry,
            packageLegal: summary.genrePackageLegal || legalText
          };
          genrePackageList.push(genreEntry);
          genrePackages.push({
            id: pkgId,
            availability
          }); // Add enriched reference
          processedIds.add(pkgId);
        }
      }
    });
  };

  // Process in priority order: included -> addon -> promo
  processPackages(channel.packageListSystem, "included");
  processPackages(channel.addonPackageList, "addon");
  processPackages(channel.promoPackageList, "promo");

  return {
    signaturePackageList,
    genrePackageList,
    signaturePackages,
    genrePackages,
    hasIncludedPackages,
    isAddonOnly,
    isPromoOnly
  };
};

/**
 * Parse channels for V2 - builds complete channel structure in one pass
 * Returns both the channel list and a map of channel-specific SVOD promo text
 */
export const parseChannelsForV2 = (args: ParseChannelsV2Args): { channels: ChannelCardV2[]; svodPromoMap: SvodPromoMap } => {
  try {
    logAdapterEvent(HELPER_NAME, "parsing channels for V2", {
      channelsCount: args.channels?.length,
      packagesCount: args.packages?.length,
      chnlSrc: args.chnlSrc
    });

    // Get genre package IDs dynamically from packageConfig
    const genreBasePacks = getPackageIdsByType({
      type: "genre",
      packageConfig: args.packageConfig
    });
    const genreMiniPacks = getPackageIdsByType({
      type: "genre-mini",
      packageConfig: args.packageConfig
    });

    const isMyFreeDTVEnabled = args.myFreeDTVConfig?.enableForInineChannelInteractiveTool;
    const isGenrePackEnabled = Boolean(args.displayGenrePack);
    const parseData: ChannelCardV2[] = [];
    const svodPromoMap: SvodPromoMap = {}; // Build the SVOD promo map here

    // Satellite context for this tool instance — same gate used below to drop
    // non-satellite channels. FAST channels are app/online-only and not
    // receivable via satellite hardware, so they're routed into MyFreeDIRECTV
    // instead of being dropped outright (mirrors channel-lineup-parser.ts /
    // channel-compare-tool-page-adapter.ts).
    const isSatelliteContext =
      args.chnlSrc.toLowerCase() === "dtvclup" ||
      args.chnlSrc.toLowerCase() === "satelliteclup";

    for (const c of args.channels) {
      // Skip inactive channels — mirrors V1 filterChannels IsActive guard
      if (!c.IsActive) {
        continue;
      }
      // FAST-flagged in satellite context: bucketed into MyFreeDIRECTV below
      // (isSatellite/isMyFreeDTV), never dropped outright.
      const isSatelliteFastChannel = isFastChannelForMyFree(c, isSatelliteContext);
      // Filter out SD channels (case-insensitive — CMS may send "SD", "sd", etc.)
      // Generated by Copilot- Start - 2026-04-01 18:39:39
      // Raw CMS channel objects carry the resolution value in `displayResolutionLabel`,
      // NOT in `resolution` (that mapping only exists in the V1 CustomChannelFeed shape).
      // Checking `c.resolution` was always undefined here → SD channels were never filtered.
      const channelResolution = c.displayResolutionLabel || c.resolution;
      if (channelResolution && String(channelResolution).toUpperCase() === "SD") {
        continue;
      }
      // Generated by Copilot- End - 2026-04-01 18:39:39
      // Filter excluded packages
      let packageList = c.packageListSystem;
      if (packageList?.length > 0 && c.excludedPackageList?.length > 0) {
        packageList = packageList.filter(
          (packge: string) => !c.excludedPackageList.includes(packge)
        );
      }

      // Single package filtering
      const targetPackageId = args.singlePackageConfig?.packageId;
      const isSinglePackageFilteringEnabled = Boolean(args.isSinglePackageVariant && targetPackageId);
      const channelIncludesTargetPackage = Array.isArray(packageList) &&
        packageList.some(pkg => pkg === targetPackageId);

      if (isSinglePackageFilteringEnabled && !channelIncludesTargetPackage) {
        continue;
      }

      // Collect all channel packages
      let chnlPackages: string[] = [];
      if (c.packageListSystem && Array.isArray(c.packageListSystem)) {
        chnlPackages = chnlPackages.concat(c.packageListSystem);
      }
      if (c.addonPackageList && Array.isArray(c.addonPackageList)) {
        chnlPackages = chnlPackages.concat(c.addonPackageList);
      }
      if (c.promoPackageList && Array.isArray(c.promoPackageList)) {
        chnlPackages = chnlPackages.concat(c.promoPackageList);
      }

      // Determine channel flags
      // FAST-only rows carry no DTV_ package token (tagged via the "FAST
      // Channels" category or "DTVE FAST_ONLY" package instead), so the token
      // check alone would mislabel them as non-satellite and drop them at the
      // isSatelliteContext gate below.
      const isSatellite = chnlPackages.some(pack => pack.includes("DTV_")) || isSatelliteFastChannel;
      let isMyFreeDTV = isSatelliteFastChannel;
      let isGenreBasePack = false;
      let isGenreMiniPack = false;

      for (const pkg of chnlPackages) {
        if (isMyFreeDTVEnabled && !isMyFreeDTV) {
          isMyFreeDTV = pkg.includes("MYFREE_DTV");
        }
        if (isGenrePackEnabled && !isGenreBasePack) {
          isGenreBasePack = genreBasePacks.includes(pkg);
        }
        if (isGenrePackEnabled && !isGenreMiniPack) {
          isGenreMiniPack = genreMiniPacks.includes(pkg);
        }
      }

      // Build signature and genre package lists
      const {
        signaturePackages,
        genrePackages,
        hasIncludedPackages
      } = buildPackageLists(
        c,
        args.packageSummaries,
        args.chnlSrc,
        svodPromoMap, // Pass the map to be populated
        args.genreConfig,
        args.svodDetails,
        args.svodPcpDetails
      );

      // Skip channel if it has NO eligible packages for current chnlSrc
      // EXCEPTIONS:
      // 1. Don't skip channels with hasIncludedPackages flag - those are SVOD tiles
      //    whose packages are populated separately via buildSvodPackageLists
      // 2. Don't skip channels in single-package variants - they passed single-package
      //    filtering above, so they're valid even if they have no signature/genre packages
      //    (e.g. MyFree channels only have MYFREE_DTV package)
      // 3. Don't skip FAST-only satellite channels - they carry no signature/genre
      //    package membership at all (see isSatelliteFastChannel above), same as a
      //    pure MyFree channel above.
      // This handles cases like "Cinemax West" which only has satellite packages
      // but component is in stream mode (chnlSrc: "streamclup")
      const hasNoPackages = signaturePackages.length === 0 && genrePackages.length === 0;
      const shouldSkip = hasNoPackages && !hasIncludedPackages && !args.isSinglePackageVariant && !isSatelliteFastChannel;
      if (shouldSkip) {
        continue;
      }

      // Skip channel if it ONLY has MyHomeTeam package
      // Exception: Allow MyHomeTeam-only channels in genre variant
      // MyHomeTeam channels should only appear in genre-specific listings
      const isMyHomeTeamOnly = genrePackages.length === 1 &&
                               genrePackages[0].id === "ATT_TV_MYHOMETEAM" &&
                               signaturePackages.length === 0;

      if (isMyHomeTeamOnly && !args.isGenreVariant) {
        continue;
      }

      // Filter for MyFree DTV if needed (check flag before pushing)
      if (args.showMyFreeDTVChannels && !isMyFreeDTV) {
        continue;
      }

      // Filter by satellite/stream if needed (check flags before pushing).
      // Reuses isSatelliteContext (computed once above) — same gate.
      if (isSatelliteContext && !isSatellite) {
        continue;
      }

      // Build minimal channel object (only properties needed by client)
      // When stripPackageRefs is true, signaturePackages/genrePackages are omitted entirely —
      // not even sent as empty arrays — since the channel modal is disabled.
      //
      // Resolve the channel's SINGLE representative category bucket key. Flag-ON
      // the channels-service ships multi-valued `categories[]` (Mongo prose); the
      // visible grid buckets a channel under EVERY accordion its categories match
      // (bucketChannelsByCategory), but the addon/hidden grouping below + the
      // client search index need ONE stable key — and it must equal the bucket key
      // the grid uses, so resolveChannelCategoryKey matches `categories[]` against
      // the package-config `engCategories` and returns the first matching label.
      // This drops the lineup family's reliance on the feed's primaryCategory slug.
      //
      // Flag-OFF / AEM: channels carry no `categories[]`, so resolveChannelCategoryKey
      // returns the legacy single `categoryId` slug untouched — preserving prod
      // behavior (and the MUSIC / ENTERTAINMENT_AND_MUSIC -> ENTERTAINMENT merge).
      // Generated by Copilot- Start - 2026-04-01 16:34:07
      const legacyCategoryId =
        c.categoryId === "MUSIC" || c.categoryId === "ENTERTAINMENT_AND_MUSIC"
          ? "ENTERTAINMENT"
          : c.categoryId;
      // Generated by Copilot- End - 2026-04-01 16:34:07
      const resolvedCategoryId = resolveChannelCategoryKey(
        c.categories,
        args.categoryConfig ?? [],
        legacyCategoryId
      );
      const channelCard: ChannelCardV2 = {
        id: c.channelId,
        callSign: c.channelCallLetterLabel,
        label: c.channelNameLabel,
        displayText: c.channelNameLabel, // For search feature - same as label
        logo: c.channelLogoPath,
        // WAT-11362: carry the server's OV decision to the render site.
        isOvLogo: Boolean(c?.isOvLogo),
        categoryId: resolvedCategoryId,
        categories: c.categories ?? [],
        hasIncludedPackages,
        isMyFreeDTV, // Needed for MyFree modal routing
        isGenreAddonChannel: isChannelEligibleForGenreAddon(c.channelCallLetterLabel, args.genreAddonChannelSet) ||
                             isChannelEligibleForGenreAddon(c.channelNameLabel, args.genreAddonChannelSet),
        ...(args.stripPackageRefs ? {} : {
          signaturePackages,
          genrePackages
        })
      };

      parseData.push(channelCard);
    }

    return {
      channels: parseData,
      svodPromoMap
    };
  } catch (error) {
    logError(`[${HELPER_NAME}] Error parsing channels:`, error);
    return {
      channels: [],
      svodPromoMap: {}
    };
  }
};

// ============================================================================
// SECTION 2: CHANNEL GROUPING & PARTITIONING
// ============================================================================

/**
 * Sorts channels alphabetically by display name.
 * Matches V1 behavior for consistent channel ordering.
 */
const sortChannelsAlphabetically = (channels: ChannelCardV2[]): ChannelCardV2[] => [...channels].sort((a, b) => {
  const nameA = (a.label || "").toLowerCase();
  const nameB = (b.label || "").toLowerCase();
  return nameA.localeCompare(nameB);
});

interface PartitionArgs {
  channels: ChannelCardV2[];
  categoryLookup: Record<string, KeyValuePair>;
}

export const partitionChannelsByVisibility = ({
  channels,
  categoryLookup
}: PartitionArgs): ChannelsPartitionResult => {
  const hiddenByCategory: Record<string, HiddenCategoryGroup> = {};
  const visible: ChannelCardV2[] = [];

  channels.forEach(channel => {
    // Channel is visible if it has included packages (using new flag)
    if (channel.hasIncludedPackages) {
      visible.push(channel);
      return;
    }

    // Hidden channel - categorize as addon-only (default reason since we removed promo/addon flags)
    const category = categoryLookup?.[channel.categoryId];
    const group = hiddenByCategory[channel.categoryId] || {
      category: category || {
        key: channel.categoryId,
        value: channel.categoryId
      },
      channels: [],
      reason: "addon-only" as const
    };
    group.channels.push(channel);
    hiddenByCategory[channel.categoryId] = group;
  });

  return {
    visible,
    hiddenByCategory
  };
};

export const groupChannelsByCategory = (
  channels: ChannelCardV2[],
  _categoryLookup: Record<string, KeyValuePair>
): Record<string, ChannelCardV2[]> => {
  const grouped = channels.reduce((acc, channel) => {
    // Merge ENTERTAINMENT_AND_MUSIC and MUSIC into ENTERTAINMENT.
    // Primary normalization happens at parse time (parseChannelsForV2), but this
    // guard ensures correctness for any channel card arriving through other paths.
    let key = channel.categoryId || "uncategorized";
    if (key === "ENTERTAINMENT_AND_MUSIC" || key === "MUSIC") {
      key = "ENTERTAINMENT";
    }

    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(channel);
    return acc;
  }, {} as Record<string, ChannelCardV2[]>);

  // Sort channels alphabetically within each category
  Object.keys(grouped).forEach(categoryId => {
    grouped[categoryId] = sortChannelsAlphabetically(grouped[categoryId]);
  });

  return grouped;
};

// Generated by Copilot- Start - 2026-03-18 18:05:40
/**
 * Groups channels by genre package membership for genre variant.
 *
 * Uses RAW channel data — does NOT rely on signaturePackages/genrePackages on
 * ChannelCardV2 (those may be stripped).
 *
 * Algorithm (O(N×G×P): rows × genre categories × package-config lookup):
 * 1. Build a label→ChannelCardV2 lookup from the parsed channel cards.
 * 2. For each raw channel and each genre category, ask the shared isChannelInPack
 *    (WAT-12389: skips inactive rows; matches billing code, then Epoch name, then pack id,
 *    then the service alias, in the INCLUDED list only) and add the channel to that bucket.
 * 3. Sort each bucket alphabetically.
 *
 * Same channel in multiple genre packs → appears in all matching categories (intentional).
 *
 * @param rawCmsChannels  - Raw channel rows (membership read from packageListSystem)
 * @param parsedCards     - Parsed ChannelCardV2 cards (used for label/logo/etc lookup)
 * @param genreCategories - KV pairs where key = ATT_TV_MYSPORTS (full package ID from packageConfig)
 * @param packageConfig   - package-config entries, so membership also matches by billing code / Epoch name (WAT-12389)
 */
export const groupChannelsByGenrePackage = (
  rawCmsChannels: any[],
  parsedCards: ChannelCardV2[],
  genreCategories: KeyValuePair[],
  packageConfig?: IPackageConfig[]
): Record<string, ChannelCardV2[]> => {
  if (!rawCmsChannels?.length || !genreCategories?.length) {
    return {};
  }

  // Build a Set of genre package IDs from category keys
  // Keys are already full IDs like ATT_TV_MYSPORTS (from packageConfig)
  const genrePackageIdSet = new Set(genreCategories.map(cat => cat.key));

  // Build label → ChannelCardV2 lookup from parsed cards
  const cardByLabel = new Map<string, ChannelCardV2>();
  for (const card of parsedCards) {
    if (card.label) {
      cardByLabel.set(card.label, card);
    }
  }

  // Step 1: Build reverse index  packageId → ChannelCardV2[]
  const byPackageId = new Map<string, ChannelCardV2[]>();

  for (const rawChannel of rawCmsChannels) {
    // Apply the same guards as parseChannelsForV2 — inactive and SD channels must be excluded
    // even when iterating raw CMS data for genre grouping.
    if (!rawChannel.IsActive) {
      continue;
    }
    // Generated by Copilot- Start - 2026-04-01 18:39:39
    // Raw CMS objects use `displayResolutionLabel`, not `resolution` — check both for safety.
    const rawResolution = rawChannel.displayResolutionLabel || rawChannel.resolution;
    if (rawResolution && String(rawResolution).toUpperCase() === "SD") {
      continue;
    }
    // Generated by Copilot- End - 2026-04-01 18:39:39

    // Look up the parsed card for this channel (matched by channelNameLabel)
    const card = cardByLabel.get(rawChannel.channelNameLabel);
    if (!card) {
      continue;
    } // Channel was filtered out during parsing (wrong chnlSrc, etc.)

    for (const pkgId of genrePackageIdSet) {
      if (!isChannelInPack(rawChannel, pkgId, packageConfig)) {
        continue;
      }
      let list = byPackageId.get(pkgId);
      if (!list) {
        list = [];
        byPackageId.set(pkgId, list);
      }
      list.push(card);
    }
  }

  // Step 2: Map each genre category key → sorted channel list
  // Keys are already ATT_TV_MYSPORTS (from packageConfig), use directly
  const grouped: Record<string, ChannelCardV2[]> = {};
  for (const { key } of genreCategories) {
    if (!key) {
      continue;
    }
    const channelList = byPackageId.get(key) || [];
    if (channelList.length > 0) {
      grouped[key] = sortChannelsAlphabetically(channelList);
    }
  }

  return grouped;
};

export const buildAddOnChannelsByCategory = (
  hiddenByCategory: Record<string, HiddenCategoryGroup>
): Record<string, ChannelCardV2[]> => {
  const addonMap: Record<string, ChannelCardV2[]> = {};

  Object.entries(hiddenByCategory).forEach(([categoryId, group]) => {
    // Exclude LOCAL and RSN from ADD_ON_CHANNELS. The category KEY check covers the
    // CMS-driven structural section labels (which stay "LOCAL"/"RSN"); the per-channel
    // flag check is the structural membership test (a local channel injected into a
    // genre pack has categoryId === pack key, so the flag is the reliable signal).
    const isStructuralGroup =
      categoryId === "LOCAL" ||
      categoryId === "RSN" ||
      group.channels.some((ch) => ch?.isLocal || ch?.isRsn);
    if (isStructuralGroup) {
      return;
    }

    // All hidden channels are addon/promo channels (we removed the flags but kept the logic)
    if (group.channels.length > 0) {
      // Sort addon channels alphabetically
      addonMap[categoryId] = sortChannelsAlphabetically(group.channels);
    }
  });

  return addonMap;
};

// ============================================================================
// SECTION 3: CHANNEL NORMALIZATION - Reduce payload size
// ============================================================================

export interface NormalizedChannelData {
  channelMap: Record<string, ChannelCardV2>; // Key: channel.label (name)
  channelsByCategory: Record<string, string[]>; // category -> channel names
  addOnChannelsByCategory: Record<string, string[]>; // category -> channel names
  allChannels: string[]; // All channel names for search
  allHiddenChannels: string[]; // Hidden channel names
  allAddOnChannels: string[]; // Add-on channel names
}

/**
 * Normalizes channel data by creating a channelMap and converting all arrays
 * to string arrays (channel names).
 *
 * Benefits:
 * - 58-60% payload reduction (arrays of strings vs arrays of objects)
 * - O(1) lookup time for accessing channel data
 * - No duplication across multiple arrays/categories
 *
 * Note: LOCAL/RSN channels are excluded from normalization as they're fetched
 * dynamically on zipcode change and handled separately.
 *
 * @param params - Original channel data with full objects
 * @returns Normalized structure with channelMap and string array references
 */
export function normalizeChannelData(params: {
  channelsByCategory: Record<string, ChannelCardV2[]>;
  addOnChannelsByCategory: Record<string, ChannelCardV2[]>;
  allChannels: ChannelCardV2[];
  allHiddenChannels: ChannelCardV2[];
  allAddOnChannels: ChannelCardV2[];
}): NormalizedChannelData {
  const {
    channelsByCategory,
    addOnChannelsByCategory,
    allChannels,
    allHiddenChannels,
    allAddOnChannels
  } = params;

  // Build channelMap - single source of truth
  // Key: channel.label (channelNameLabel from CMS)
  // Value: full ChannelCardV2 object
  const channelMap: Record<string, ChannelCardV2> = {};

  // Generated by Copilot- Start - 2026-03-18 18:24:53
  // Collect all unique channels (from all sources)
  const allUniqueChannels = new Map<string, ChannelCardV2>();

  // Add from allChannels (visible + hidden)
  allChannels.forEach(channel => {
    if (channel.label) {
      allUniqueChannels.set(channel.label, channel);
    }
  });

  // Add from hidden channels (in case any were missed)
  allHiddenChannels.forEach(channel => {
    if (channel.label) {
      allUniqueChannels.set(channel.label, channel);
    }
  });

  // Add from add-on channels
  allAddOnChannels.forEach(channel => {
    if (channel.label) {
      allUniqueChannels.set(channel.label, channel);
    }
  });

  // Also populate from channelsByCategory — in genre variant allChannels/allHiddenChannels
  // are intentionally empty (search disabled), but all channels still live in channelsByCategory.
  // Without this, channelMap ends up empty for genre variant and the component can't render.
  Object.values(channelsByCategory).forEach(channels => {
    channels.forEach(channel => {
      if (channel.label && !allUniqueChannels.has(channel.label)) {
        allUniqueChannels.set(channel.label, channel);
      }
    });
  });

  // Convert Map to Record
  allUniqueChannels.forEach((channel, label) => {
    channelMap[label] = channel;
  });
  // Generated by Copilot- End - 2026-03-18 18:24:53

  // Normalize channelsByCategory - convert to string arrays
  const normalizedChannelsByCategory: Record<string, string[]> = {};
  Object.entries(channelsByCategory).forEach(([category, channels]) => {
    normalizedChannelsByCategory[category] = channels
      .map(ch => ch.label)
      .filter((label): label is string => Boolean(label));
  });

  // Normalize addOnChannelsByCategory - convert to string arrays
  const normalizedAddOnChannelsByCategory: Record<string, string[]> = {};
  Object.entries(addOnChannelsByCategory).forEach(([category, channels]) => {
    normalizedAddOnChannelsByCategory[category] = channels
      .map(ch => ch.label)
      .filter((label): label is string => Boolean(label));
  });

  // Normalize flat arrays - convert to string arrays
  const normalizedAllChannels = allChannels
    .map(ch => ch.label)
    .filter((label): label is string => Boolean(label));

  const normalizedAllHiddenChannels = allHiddenChannels
    .map(ch => ch.label)
    .filter((label): label is string => Boolean(label));

  const normalizedAllAddOnChannels = allAddOnChannels
    .map(ch => ch.label)
    .filter((label): label is string => Boolean(label));

  return {
    channelMap,
    channelsByCategory: normalizedChannelsByCategory,
    addOnChannelsByCategory: normalizedAddOnChannelsByCategory,
    allChannels: normalizedAllChannels,
    allHiddenChannels: normalizedAllHiddenChannels,
    allAddOnChannels: normalizedAllAddOnChannels
  };
}

/**
 * Helper to reconstruct full channel objects from string array using channelMap
 * Used in components that need full channel objects
 *
 * @param channelNames - Array of channel names (labels)
 * @param channelMap - Map of channel name -> full channel object
 * @returns Array of full ChannelCardV2 objects
 */
export function denormalizeChannels(
  channelNames: string[],
  channelMap: Record<string, ChannelCardV2>
): ChannelCardV2[] {
  return channelNames
    .map(name => channelMap[name])
    .filter((channel): channel is ChannelCardV2 => Boolean(channel));
}

/**
 * Helper to denormalize categorized channels
 *
 * @param categorizedNames - Record of category -> channel names
 * @param channelMap - Map of channel name -> full channel object
 * @returns Record of category -> full channel objects
 */
export function denormalizeCategorizedChannels(
  categorizedNames: Record<string, string[]>,
  channelMap: Record<string, ChannelCardV2>
): Record<string, ChannelCardV2[]> {
  const result: Record<string, ChannelCardV2[]> = {};

  Object.entries(categorizedNames).forEach(([category, names]) => {
    result[category] = denormalizeChannels(names, channelMap);
  });

  return result;
}

// ============================================================================
// SECTION 4: GENRE ADDON ELIGIBILITY
// ============================================================================

/**
 * Normalizes channel name for comparison (matches V1 logic)
 */
const normalizeChannelName = (name: string | undefined): string => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  return name
    .replace(/&amp;|\s*\(\d+\)\s*|<\/?sup>|<\/?div>|<\/?p>|east|hd|™|[^\w\s]/gi, '')
    .replace(/\s+/g, '')
    .toLowerCase()
    .trim();
};

/**
 * Parses HTML channel list string into array of channel names
 */
const parseChannelList = (htmlString: string): string[] => String(htmlString)
  .split('</p>')
  .map(part => part.replace(/<[^>]*>/g, '').trim())
  .filter(text => text.length > 0);

/**
 * Processes svodGenreAddon data to extract and normalize channel lists
 */
const processSvodGenreAddon = (svodGenreAddon: any[]): any[] => {
  if (!Array.isArray(svodGenreAddon)) {
    return [];
  }

  return svodGenreAddon.map(item => ({
    ...item,
    channelList: item?.channelList
      ?.flatMap(htmlString => parseChannelList(htmlString))
      ?.map(normalizeChannelName) || []
  }));
};

/**
 * Builds a Set of normalized channel names that are eligible for "Genre Packs" add-on
 *
 * Logic (matching V1):
 * 1. For each genre pack in svodGenrePkgList, get its SVOD offer code
 * 2. Find matching SVOD addon with that offer code in svodGenreAddon
 * 3. Collect all channel names from those addons
 * 4. Return as a Set for fast lookup
 *
 * @param svodGenrePkgList - Map of package IDs to SVOD offer codes (from EPOCH)
 * @param svodGenreAddon - Array of SVOD addons with channel lists (from CMS)
 * @returns Set of normalized channel names eligible for genre addon
 */
export const buildGenreAddonChannelSet = (
  svodGenrePkgList: Record<string, string> | undefined,
  svodGenreAddon: any[] | undefined
): Set<string> => {
  const eligibleChannels = new Set<string>();

  if (!svodGenrePkgList || !svodGenreAddon) {
    return eligibleChannels;
  }

  // Process addon data to normalize channel lists
  const processedSvodGenreAddon = processSvodGenreAddon(svodGenreAddon);

  // For each genre pack, collect channels from matching SVOD addons
  Object.values(svodGenrePkgList).forEach(offerCode => {
    processedSvodGenreAddon.forEach(addon => {
      if (addon?.productSKU === offerCode && addon?.channelList) {
        addon.channelList.forEach(channelName => {
          if (channelName) {
            eligibleChannels.add(channelName);
          }
        });
      }
    });
  });

  return eligibleChannels;
};

/**
 * Checks if a channel is eligible for "Genre Packs" add-on
 *
 * @param channelName - Display name of the channel
 * @param genreAddonChannelSet - Pre-built set of eligible channel names
 * @returns true if channel is in the set, false otherwise
 */
export const isChannelEligibleForGenreAddon = (
  channelName: string | undefined,
  genreAddonChannelSet: Set<string> | undefined
): boolean => {
  if (!channelName || !genreAddonChannelSet || genreAddonChannelSet.size === 0) {
    return false;
  }

  const normalizedName = normalizeChannelName(channelName);
  return genreAddonChannelSet.has(normalizedName);
};
// Generated by Copilot- End
